import { memo } from 'react';
import { Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import AuthExpiryBanner from './AuthExpiryBanner.jsx';
import { STATUS_TIER } from '../constants';
import { AlertTriangle, ShieldAlert, ArrowRight } from 'lucide-react';

// Non-auth `needs_attention` states → banner CTA + where the CTA goes. Auth
// states are delegated to CT-4198's AuthExpiryBanner so that copy stays single-
// sourced. `schema_drift`/`needs_mapping` hand off to Data Studio; the provider
// states link out to the provider.
const GATE_STATES = {
  schema_drift: { cta: 'reviewChanges', action: 'dataStudio' },
  needs_mapping: { cta: 'resolveMapping', action: 'dataStudio' },
  rate_limited: { cta: 'openQuota', action: 'provider' },
  provider_down: { cta: 'checkStatus', action: 'provider' },
};

// Mirrors AuthExpiryBanner's `.gate-banner` tones so every state on the page
// reads the same. Tone (warn amber / block red) comes from STATUS_TIER.
const TONE = {
  red: {
    wrap: 'border-[color-mix(in_srgb,#EF4444_28%,transparent)] bg-[color-mix(in_srgb,#EF4444_8%,transparent)]',
    iconBox: 'bg-[rgba(239,68,68,0.14)] text-[#EF4444]',
    chip: '!border-[rgba(239,68,68,0.36)] !bg-[rgba(239,68,68,0.18)] !text-[#EF4444]',
  },
  amber: {
    wrap: 'border-[color-mix(in_srgb,#FDC700_28%,transparent)] bg-[color-mix(in_srgb,#FDC700_8%,transparent)]',
    iconBox: 'bg-[rgba(253,199,0,0.14)] text-[#FDC700]',
    chip: '!border-[rgba(253,199,0,0.36)] !bg-[rgba(253,199,0,0.18)] !text-[#FDC700]',
  },
};

const CHIP_BASE =
  '!h-[18px] !gap-0 !px-[7px] !py-0 !text-[10px] !font-extrabold !uppercase !leading-none !tracking-[0.04em]';

/**
 * Provider Detail gate banner (CT-4178) — prototype `gateBannerHTML`. Surfaces
 * the gating action for a `needs_attention` provider above the sticky header.
 * Auth states render CT-4198's `AuthExpiryBanner`; the four non-auth states
 * (`schema_drift`, `needs_mapping`, `rate_limited`, `provider_down`) render here
 * with per-state copy + CTA. Tone follows `STATUS_TIER` (amber warn / red block)
 * and the chip reuses the shared `authBanner` use-with-caution / blocked labels.
 */
function GateBanner({
  provider,
  onRotate,
  onReview,
  onOpenProvider,
  canManage = true,
  className = '',
}) {
  const { t } = useTranslation('admin-studio');
  const status = provider?.status;

  if (status === 'auth_expiring' || status === 'auth_expired') {
    return (
      <AuthExpiryBanner
        status={status}
        providerName={provider.name}
        onRotate={onRotate}
        canManage={canManage}
        className={className}
      />
    );
  }

  const config = GATE_STATES[status];
  if (!config) return null;

  const isBlock = STATUS_TIER[status] === 'red';
  const tone = isBlock ? TONE.red : TONE.amber;
  const Icon = isBlock ? ShieldAlert : AlertTriangle;
  const onClick = config.action === 'dataStudio' ? onReview : onOpenProvider;

  return (
    <div
      role="alert"
      className={['flex items-center gap-4 rounded-[10px] border px-[18px] py-[14px]', tone.wrap, className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        aria-hidden="true"
        className={['flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[9px]', tone.iconBox].join(' ')}
      >
        <Icon size={18} strokeWidth={1.8} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-[3px] flex flex-wrap items-center gap-2.5 text-[14px] font-bold tracking-[0.01em] text-content">
          <span>{t(`integrations.status.gateBanner.${status}.title`, { name: provider.name })}</span>
          <Badge
            size="small"
            radius="full"
            variant={isBlock ? 'danger' : 'warning'}
            className={[CHIP_BASE, tone.chip].join(' ')}
          >
            {t(
              isBlock
                ? 'integrations.status.authBanner.useBlocked'
                : 'integrations.status.authBanner.useWithCaution',
            )}
          </Badge>
        </div>
        <p className="text-[13px] leading-[1.5] text-content-secondary dark:text-white/60">
          {t(`integrations.status.gateBanner.${status}.body`)}
        </p>
      </div>

      <div className="shrink-0">
        <Button variant="classicPrimary" size="sm" rightIcon={ArrowRight} onClick={onClick}>
          {t(`integrations.status.gateBanner.cta.${config.cta}`)}
        </Button>
      </div>
    </div>
  );
}

export default memo(GateBanner);
